import { useQuery } from "@tanstack/react-query";
import { Crop, DropSize, Task, TillingDepth } from "./constants";
import { karlFarmGet } from "./queryClient";

// Route: GET /data/tasks?{from}={timestamp}&{to}={timestamp}
// API Docs: https://documenter.getpostman.com/view/16381164/UVksNF2z#2f06146c-ff8f-488a-93ef-fecbdbe63c0b

type FromType = "earliest_after" | "start_after" | "deadline_after";
type ToType = "earliest_before" | "start_before" | "deadline_before";

export const getUseAllTasksQueryKey = () => {
  const fromType: FromType = "start_after";
  const fromTimestamp = 0;
  const toType: ToType = "start_before";
  const toTimestamp = 20000000000;
  return [`/data/tasks?${fromType}=${fromTimestamp}&${toType}=${toTimestamp}`];
};

export const useAllTasks = () => {
  return useQuery(getUseAllTasksQueryKey(), {
    queryFn: () =>
      karlFarmGet<AllTasksResponse>(getUseAllTasksQueryKey()[0]).catch(
        (error) => {
          if (error?.response?.status === 404) {
            // Patch 404 response to return empty array
            const emptyResponse: AllTasksResponse = {
              messages: { status: "OK" },
              data: { tasks: [], info: { returned_count: 0 } },
            };
            return emptyResponse;
          }
          throw error;
        }
      ),
  });
};

export type AllTasksResponse = {
  messages: { status: string };
  data?: Data;
};

export type Data = {
  tasks: TaskElement[];
  info: { returned_count: number };
};

export type TaskElement = {
  completed: boolean;
  fields: Array<{ id: string }>;
  task_id: string;
  machines: Array<{ id: string }>;
  "consumer_id#enterprise_id": `${string}#${string}`;
  created: number;
  custom_attributes: Record<string, unknown>;
  attributes: Attributes;
  time: Time;
  id: string;
  users: Array<{ id: string }>;
};

export type Attributes = {
  title?: string | null;
  task: Task;
  crop?: Crop;
  tilling_depth?: TillingDepth;
  drop_size?: DropSize;
  notes?: string;
};

export type Time = {
  /** UNIX epoch in seconds */
  start: number;
  /** In minutes */
  duration: number;
  /** UNIX epoch in seconds */
  deadline: number | null;
  /** UNIX epoch in seconds */
  earliest: number | null;
};
